import { ThemeEvents } from '@theme/events';

/**
 * Formats a price value in cents to a dollar amount
 * @param {number} cents - Price in cents
 * @returns {number} Price in dollars
 */
function formatPrice(cents) {
  return Math.round(cents) / 100;
}

/**
 * Formats a number as currency
 * @param {number} amount - Amount in dollars
 * @returns {string} Formatted currency string
 */
function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Fetches cart data from Shopify API
 * @returns {Promise<Object|null>} Cart data or null if error
 */
async function fetchCartData() {
  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`);
    if (!response.ok) {
      throw new Error('Failed to fetch cart data');
    }
    const data = await response.json();
    return data && typeof data === 'object' ? data : null;
  } catch (error) {
    console.error('Error fetching cart data:', error);
    return null;
  }
}

/**
 * Checks if free shipping discount is applied to the cart
 * @param {Object} cartData - Cart data from Shopify API
 * @returns {boolean} True if free shipping discount is applied
 */
function hasFreeShippingDiscount(cartData) {
  if (!cartData || typeof cartData !== 'object') {
    return false;
  }

  // Check shipping discounts
  if ('shipping_discount_applications' in cartData && Array.isArray(cartData.shipping_discount_applications)) {
    for (const discount of cartData.shipping_discount_applications) {
      if (discount && typeof discount === 'object') {
        if ('target_type' in discount && discount.target_type === 'shipping') {
          return true;
        }
        const title = discount.title || '';
        const description = discount.description || '';
        if (title.toLowerCase().includes('free shipping') || description.toLowerCase().includes('free shipping')) {
          return true;
        }
      }
    }
  }

  // Check discount applications
  if ('discount_applications' in cartData && Array.isArray(cartData.discount_applications)) {
    for (const discount of cartData.discount_applications) {
      if (discount && typeof discount === 'object') {
        // Check shipping discount
        if ('target_type' in discount && discount.target_type === 'shipping') {
          return true;
        }
        // Check title/description
        const title = discount.title || '';
        const description = discount.description || '';
        if (title.toLowerCase().includes('free shipping') || description.toLowerCase().includes('free shipping')) {
          return true;
        }
      }
    }
  }

  // Check cart level discounts
  if ('cart_level_discount_applications' in cartData && Array.isArray(cartData.cart_level_discount_applications)) {
    for (const discount of cartData.cart_level_discount_applications) {
      if (discount && typeof discount === 'object') {
        if ('target_type' in discount && discount.target_type === 'shipping') {
          return true;
        }
        const title = discount.title || '';
        const description = discount.description || '';
        if (title.toLowerCase().includes('free shipping') || description.toLowerCase().includes('free shipping')) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * Fetches shipping rates which may include free shipping thresholds
 * @param {string} country - Country code (default: US)
 * @param {string} province - Province/state code (optional)
 * @returns {Promise<Object|null>} Shipping rates or null if error
 */
async function fetchShippingRates(country = 'US', province = '') {
  try {
    // Get cart token
    const cartData = await fetchCartData();
    if (!cartData || typeof cartData !== 'object' || !('token' in cartData)) {
      return null;
    }

    // Build estimate URL
    const estimateUrl = `${Theme.routes.cart_url}/shipping_rates.json`;
    const params = new URLSearchParams();
    params.append(`shipping_address[country]`, country);
    
    if (province) {
      params.append('shipping_address[province]', province);
    }

    const response = await fetch(`${estimateUrl}?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      // Optional - may not be available without address
      return null;
    }

    const data = await response.json();
    return data && typeof data === 'object' ? data : null;
  } catch (error) {
    // Silently fail - shipping rates are optional
    return null;
  }
}

/**
 * Extracts free shipping threshold from cart data
 * Looks for shipping discount applications or shipping rates with minimum order requirements
 * @param {Object} cartData - Cart data from Shopify API
 * @returns {number|null} Threshold in cents, or null if not found
 */
function extractFreeShippingThreshold(cartData) {
  if (!cartData || typeof cartData !== 'object') {
    return null;
  }

  // Debug log (dev only)
  if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
    console.log('[Free Shipping Progress] Checking cart data for threshold:', cartData);
  }

  // Check shipping discounts (most common)
  if ('shipping_discount_applications' in cartData && Array.isArray(cartData.shipping_discount_applications) && cartData.shipping_discount_applications.length > 0) {
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
      console.log('[Free Shipping Progress] Found shipping_discount_applications:', cartData.shipping_discount_applications);
    }
    for (const discount of cartData.shipping_discount_applications) {
      // Check minimum requirement
      if (discount && typeof discount === 'object') {
        // Check minimum_subtotal (cents)
        if ('minimum_subtotal' in discount) {
          const minSubtotal = discount.minimum_subtotal;
          if (typeof minSubtotal === 'number' && minSubtotal > 0) {
            return minSubtotal;
          }
          // Handle string format
          if (typeof minSubtotal === 'string') {
            const parsed = parseFloat(minSubtotal);
            if (!isNaN(parsed) && parsed > 0) {
              // Convert dollars to cents if needed
              return parsed < 100 ? parsed * 100 : parsed;
            }
          }
        }
        // Check other fields
        if ('minimum_purchase_amount' in discount) {
          const minAmount = discount.minimum_purchase_amount;
          if (typeof minAmount === 'number' && minAmount > 0) {
            return minAmount < 100 ? minAmount * 100 : minAmount;
          }
        }
      }
    }
  }

  // Check shipping rates for free shipping
  if ('shipping_rates' in cartData && Array.isArray(cartData.shipping_rates)) {
    for (const rate of cartData.shipping_rates) {
      if (rate && typeof rate === 'object') {
        // Check for free shipping rate
        const ratePrice = rate.price;
        const isFree = ratePrice === 0 || ratePrice === '0.00' || ratePrice === 0.00 || (typeof ratePrice === 'number' && ratePrice === 0);
        if (isFree) {
          // Check minimum order
          if ('minimum_order_amount' in rate) {
            const minAmount = rate.minimum_order_amount;
            if (typeof minAmount === 'number' && minAmount > 0) {
              // Convert dollars to cents if needed
              return minAmount < 100 ? minAmount * 100 : minAmount;
            }
            if (typeof minAmount === 'string') {
              const parsed = parseFloat(minAmount);
              if (!isNaN(parsed) && parsed > 0) {
                return parsed < 100 ? parsed * 100 : parsed;
              }
            }
          }
          // Check minimum_subtotal
          if ('minimum_subtotal' in rate) {
            const minSubtotal = rate.minimum_subtotal;
            if (typeof minSubtotal === 'number' && minSubtotal > 0) {
              return minSubtotal < 100 ? minSubtotal * 100 : minSubtotal;
            }
          }
        }
      }
    }
  }

  // Check discount applications
  if ('discount_applications' in cartData && Array.isArray(cartData.discount_applications)) {
    for (const discount of cartData.discount_applications) {
      if (discount && typeof discount === 'object') {
        // Check shipping discount
        if ('target_type' in discount && discount.target_type === 'shipping') {
          // Check minimum
          if ('minimum_subtotal' in discount && typeof discount.minimum_subtotal === 'number') {
            return discount.minimum_subtotal;
          }
        }
      }
    }
  }

  // Check cart level discounts
  if ('cart_level_discount_applications' in cartData && Array.isArray(cartData.cart_level_discount_applications)) {
    for (const discount of cartData.cart_level_discount_applications) {
      if (discount && typeof discount === 'object') {
        // Check shipping discounts
        if ('target_type' in discount && discount.target_type === 'shipping') {
          if ('minimum_subtotal' in discount && typeof discount.minimum_subtotal === 'number') {
            return discount.minimum_subtotal;
          }
        }
      }
    }
  }

  return null;
}

/**
 * Updates the free shipping progress bar
 * @param {HTMLElement} progressElement - The progress bar element
 * @param {number} cartTotal - Current cart total in cents
 * @param {number} threshold - Free shipping threshold in cents
 * @param {boolean} [hasFreeShipping] - Whether free shipping discount is applied (optional)
 */
function updateProgressBar(progressElement, cartTotal, threshold, hasFreeShipping = false) {
  let messageText = progressElement.querySelector('.free-shipping-progress__message-text');
  let amountElement = progressElement.querySelector('.free-shipping-progress__amount-value');
  const fillElement = progressElement.querySelector('.free-shipping-progress__bar-fill');
  
  if (!fillElement || !(fillElement instanceof HTMLElement)) {
    return;
  }

  // Hide if cart empty
  if (!cartTotal || cartTotal === 0) {
    progressElement.style.display = 'none';
    return;
  }

  const cartTotalDollars = formatPrice(cartTotal);
  const thresholdDollars = formatPrice(threshold);
  const remaining = Math.max(0, thresholdDollars - cartTotalDollars);
  
  // Show progress bar
  progressElement.style.display = 'block';
  
  // Check if message-text was replaced (e.g., by Shopify accelerated checkout)
  const messageContainer = progressElement.querySelector('.free-shipping-progress__message');
  if (messageContainer && !messageText) {
    // Message text was replaced - restore it
    const shopifyCheckout = messageContainer.querySelector('shopify-accelerated-checkout-cart');
    const flagElement = messageContainer.querySelector('.free-shipping-progress__bar-flag');
    
    messageText = document.createElement('span');
    messageText.className = 'free-shipping-progress__message-text';
    messageText.setAttribute('data-skip-node-update', '');
    
    if (flagElement && flagElement.parentNode === messageContainer) {
      messageContainer.insertBefore(messageText, flagElement);
    } else if (shopifyCheckout && shopifyCheckout.parentNode === messageContainer) {
      messageContainer.insertBefore(messageText, shopifyCheckout);
    } else {
      const firstElement = messageContainer.querySelector('*');
      if (firstElement && firstElement.parentNode === messageContainer) {
        messageContainer.insertBefore(messageText, firstElement);
      } else {
        messageContainer.insertBefore(messageText, messageContainer.firstChild);
      }
    }
  }
  
  // Show success if free shipping achieved
  if (hasFreeShipping || cartTotal >= threshold) {
    // Get success message
    const successMessage = progressElement.dataset.successMessage || 'You got free shipping';
    
    // Escape HTML
    const escapedMessage = successMessage
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
    
    // Update message
    if (messageText) {
      messageText.innerHTML = '<span class="free-shipping-progress__text">' + escapedMessage + '</span>';
    }
    
    // Set to 100%
    fillElement.style.width = '100%';
    
    // Add success class
    progressElement.classList.add('free-shipping-progress--achieved');
    
    return;
  }

  // Remove success class
  progressElement.classList.remove('free-shipping-progress--achieved');
  
  if (messageText) {
    const textElements = messageText.querySelectorAll('.free-shipping-progress__text');
    const amountEl = messageText.querySelector('.free-shipping-progress__amount');
    
    // If structure is missing or was replaced, restore it
    if (textElements.length < 2 || !amountEl) {
      messageText.innerHTML = '<span class="free-shipping-progress__text">You are</span><strong class="free-shipping-progress__amount">$<span class="free-shipping-progress__amount-value">' + formatCurrency(remaining) + '</span></strong><span class="free-shipping-progress__text">away from free shipping</span>';
      amountElement = progressElement.querySelector('.free-shipping-progress__amount-value');
    } else {
      amountElement = messageText.querySelector('.free-shipping-progress__amount-value');
    }
    
    if (amountElement) {
      amountElement.textContent = formatCurrency(remaining);
    }
  }
  
  // Calculate progress (0-100)
  const progressPercentage = Math.min(100, (cartTotalDollars / thresholdDollars) * 100);
  
  // Update fill
  fillElement.style.width = `${progressPercentage}%`;
}

/**
 * Initializes a free shipping progress bar element
 * @param {HTMLElement} progressElement - The progress bar element
 */
async function initializeProgressBar(progressElement) {
  // Get threshold from data attribute
  let threshold = null;
  const thresholdAttr = progressElement.dataset.freeShippingThreshold;
  if (thresholdAttr) {
    const parsedThreshold = parseInt(thresholdAttr, 10);
    if (!isNaN(parsedThreshold) && parsedThreshold > 0) {
      threshold = parsedThreshold;
    }
  }
  
  // Hide if no threshold
  if (!threshold || typeof threshold !== 'number') {
    progressElement.style.display = 'none';
    return;
  }
  
  // Get cart total from data attribute
  const initialCartTotal = progressElement.dataset.cartTotal;
  let cartTotal = 0;
  if (initialCartTotal) {
    const parsed = parseInt(initialCartTotal, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      cartTotal = parsed;
    }
  }
  
  // Store threshold
  // @ts-ignore - custom property
  progressElement._threshold = threshold;
  
  const messageContainer = progressElement.querySelector('.free-shipping-progress__message');
  let messageText = progressElement.querySelector('.free-shipping-progress__message-text');
  if (messageContainer && !messageText) {
    // Restore message-text if it was replaced
    const shopifyCheckout = messageContainer.querySelector('shopify-accelerated-checkout-cart');
    const flagElement = messageContainer.querySelector('.free-shipping-progress__bar-flag');
    
    messageText = document.createElement('span');
    messageText.className = 'free-shipping-progress__message-text';
    messageText.setAttribute('data-skip-node-update', '');
    messageText.innerHTML = '<span class="free-shipping-progress__text">You are</span><strong class="free-shipping-progress__amount">$<span class="free-shipping-progress__amount-value">0</span></strong><span class="free-shipping-progress__text">away from free shipping</span>';
    
    // Insert in correct position
    if (flagElement && flagElement.parentNode === messageContainer) {
      messageContainer.insertBefore(messageText, flagElement);
    } else if (shopifyCheckout && shopifyCheckout.parentNode === messageContainer) {
      messageContainer.insertBefore(messageText, shopifyCheckout);
    } else {
      const firstElement = messageContainer.querySelector('*');
      if (firstElement && firstElement.parentNode === messageContainer) {
        messageContainer.insertBefore(messageText, firstElement);
      } else {
        messageContainer.insertBefore(messageText, messageContainer.firstChild);
      }
    }
  }
  
  // Fetch fresh cart data
  const cartData = await fetchCartData();
  
  // Check free shipping discount
  let hasFreeShipping = false;
  if (cartData) {
    hasFreeShipping = hasFreeShippingDiscount(cartData);
    // @ts-ignore - custom property
    progressElement._hasFreeShipping = hasFreeShipping;
  }
  
  // Update cart total
  if (cartData && typeof cartData === 'object' && 'total_price' in cartData) {
    const totalPrice = cartData.total_price;
    if (typeof totalPrice === 'number') {
      cartTotal = totalPrice;
      progressElement.dataset.cartTotal = String(totalPrice);
    }
  }
  
  // Update progress bar
  updateProgressBar(progressElement, cartTotal, threshold, hasFreeShipping);

  // Listen for cart updates
  /** @param {Event} event */
  const handleCartUpdate = async (event) => {
    // @ts-ignore - custom property
    const currentThreshold = progressElement._threshold;
    
    if (!currentThreshold || typeof currentThreshold !== 'number') {
      return;
    }
    
    // Wait for morphing, then read cart total from data attribute
    requestAnimationFrame(() => {
      queueMicrotask(async () => {
        // Get cart total from data attribute (updated by morphing)
        const updatedCartTotal = progressElement.dataset.cartTotal;
        let totalPrice = null;
        
        if (updatedCartTotal) {
          const parsed = parseInt(updatedCartTotal, 10);
          if (!isNaN(parsed) && parsed >= 0) {
            totalPrice = parsed;
          }
        }
        
        // Try event data if not in data attribute
        if (totalPrice === null && event && 'detail' in event) {
          // @ts-ignore - CustomEvent detail property
          const eventDetail = event.detail;
          // DiscountUpdateEvent has cart data in event.detail.resource
          if (eventDetail && typeof eventDetail === 'object' && 'resource' in eventDetail) {
            // @ts-ignore - resource property exists
            const resource = eventDetail.resource;
            if (resource && typeof resource === 'object' && 'total_price' in resource) {
              totalPrice = resource.total_price;
            }
          }
        }
        
        // Fetch as fallback
        let freshCartData = null;
        if (totalPrice === null) {
          freshCartData = await fetchCartData();
          if (freshCartData && typeof freshCartData === 'object' && 'total_price' in freshCartData) {
            totalPrice = freshCartData.total_price;
          }
        }
        
        // Validate totalPrice
        if (typeof totalPrice !== 'number') {
          return;
        }
        
        // Check free shipping discount
        let hasFreeShipping = false;
        // @ts-ignore - custom property
        if (progressElement._hasFreeShipping !== undefined) {
          // @ts-ignore - custom property
          hasFreeShipping = progressElement._hasFreeShipping;
        } else {
          // Fetch if needed
          if (!freshCartData) {
            freshCartData = await fetchCartData();
          }
          if (freshCartData) {
            hasFreeShipping = hasFreeShippingDiscount(freshCartData);
            // @ts-ignore - custom property
            progressElement._hasFreeShipping = hasFreeShipping;
          }
        }
        
        // Update progress bar
        updateProgressBar(progressElement, totalPrice, currentThreshold, hasFreeShipping);
      });
    });
  };
  
  document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
  document.addEventListener(ThemeEvents.discountUpdate, handleCartUpdate);
  
  // Store handler for cleanup
  // @ts-ignore - custom property
  progressElement._cartUpdateHandler = handleCartUpdate;
}

/**
 * Initializes all free shipping progress bars on the page
 */
function initializeAllProgressBars() {
  // Wait for DOM ready (after morphing)
  requestAnimationFrame(() => {
    const allProgressElements = document.querySelectorAll('.free-shipping-progress');
    
    allProgressElements.forEach((element) => {
      if (element instanceof HTMLElement) {
        // Remove existing listener
        // @ts-ignore - custom property
        if (element._cartUpdateHandler) {
          // @ts-ignore - custom property
          document.removeEventListener(ThemeEvents.cartUpdate, element._cartUpdateHandler);
          // @ts-ignore - custom property
          document.removeEventListener(ThemeEvents.discountUpdate, element._cartUpdateHandler);
        }
        
        initializeProgressBar(element);
      }
    });
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAllProgressBars);
} else {
  initializeAllProgressBars();
}

// Global function for dynamic loading
if (typeof window !== 'undefined') {
  // @ts-ignore - adding to window
  window.initFreeShippingProgress = function() {
    initializeAllProgressBars();
  };
}

// Listen for dialog open (cart drawer)
if (typeof window !== 'undefined') {
  // Listen for dialog:open event
  document.addEventListener('dialog:open', (event) => {
    // @ts-ignore - CustomEvent detail may exist
    const customEvent = event;
    // @ts-ignore - detail property exists on CustomEvent
    const eventDetail = customEvent.detail;
    const target = event.target || (eventDetail && eventDetail.dialog);
    if (target instanceof HTMLElement) {
      const cartDrawer = target.closest?.('cart-drawer-component');
      if (cartDrawer) {
        // Initialize when drawer opens (wait for morphing)
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              initializeAllProgressBars();
            }, 50);
          });
        });
      }
    }
  });
  
  // Observe dialog[open] attribute
  const dialogOpenObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
        const dialog = mutation.target;
        if (dialog instanceof HTMLElement && dialog.tagName === 'DIALOG') {
          const cartDrawer = dialog.closest('cart-drawer-component');
          if (cartDrawer && dialog.hasAttribute('open')) {
            // Initialize when dialog opens (wait for morphing)
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                setTimeout(() => {
                  initializeAllProgressBars();
                }, 50);
              });
            });
          }
        }
      }
    }
  });
  
  // Observe all dialogs
  if (document.body) {
    dialogOpenObserver.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ['open'],
    });
    
    // Check for already open drawers
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer) {
      const dialog = cartDrawer.querySelector('dialog[open]');
      if (dialog) {
        initializeAllProgressBars();
      }
    }
  }
  
  // Listen for cart/discount updates
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer) {
      // Retry with delays to catch progress bar after morphing
      const tryInitialize = (attempt = 1, maxAttempts = 5) => {
        const delay = attempt * 100; // 100ms, 200ms, 300ms, etc.
        
        setTimeout(() => {
          const progressBars = document.querySelectorAll('.free-shipping-progress');
          
          if (progressBars.length > 0) {
            // initializeAllProgressBars();
          } else if (attempt < maxAttempts) {
            requestAnimationFrame(() => {
              tryInitialize(attempt + 1, maxAttempts);
            });
          } else {
            // Final retry with longer delay
            setTimeout(() => {
              initializeAllProgressBars();
            }, 500);
          }
        }, delay);
      };
      
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          tryInitialize();
        });
      });
    }
  });
  
  document.addEventListener(ThemeEvents.discountUpdate, () => {
    // Update after discount changes (wait for morphing)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          // Fetch cart data to check discounts
          fetchCartData().then((cartData) => {
            if (cartData && typeof cartData === 'object' && 'total_price' in cartData) {
              const progressBars = document.querySelectorAll('.free-shipping-progress');
              progressBars.forEach((bar) => {
                if (bar instanceof HTMLElement) {
                  const hasFreeShipping = hasFreeShippingDiscount(cartData);
                  // @ts-ignore - custom property
                  bar._hasFreeShipping = hasFreeShipping;
                  // @ts-ignore - custom property
                  const threshold = bar._threshold;
                  if (threshold) {
                    // @ts-ignore - total_price property exists
                    const totalPrice = cartData.total_price;
                    const cartTotal = typeof totalPrice === 'number' ? totalPrice : 0;
                    updateProgressBar(bar, cartTotal, threshold, hasFreeShipping);
                  }
                }
              });
            }
            // Full initialization
            initializeAllProgressBars();
          });
        },300);
      });
    });
  });
}

// Re-initialize on section updates (cart drawer morphing)
if (typeof window !== 'undefined') {
  // Watch for cart drawer updates
  const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1 && node instanceof HTMLElement) {
              // Check if node is/contains progress bar
              const progressElement = node.classList?.contains('free-shipping-progress')
                ? node
                : node.querySelector?.('.free-shipping-progress');
              
              if (progressElement instanceof HTMLElement) {
                // Initialize (wait for morphing)
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    setTimeout(() => {
                      initializeProgressBar(progressElement);
                    }, 0);
                  });
                });
              }
            }
          });
        }
        
        // Check data-cart-total updates
        if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
          const progressElement = mutation.target.classList?.contains('free-shipping-progress')
            ? mutation.target
            : mutation.target.closest?.('.free-shipping-progress');
          
          if (progressElement instanceof HTMLElement && mutation.attributeName === 'data-cart-total') {
            // Cart total updated
            // @ts-ignore - custom property
            const threshold = progressElement._threshold;
            if (threshold) {
              const newCartTotal = parseInt(progressElement.dataset.cartTotal || '0', 10);
              if (!isNaN(newCartTotal)) {
                updateProgressBar(progressElement, newCartTotal, threshold);
              }
            }
          }
        }
      });
  });

  // Observe document body
  if (document.body) {
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-cart-total'],
    });
  }
}

